import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import Hls from "hls.js";
import logoWhite from "@/assets/logo-white.svg";
import { usePageSEO } from "@/hooks/use-page-seo";
import Footer from "@/components/Footer";
import LastYearSection from "@/components/LastYearSection";
import VolumeToggle from "@/components/ui/VolumeToggle";

const HERO_VIDEO_URL = "https://customer-izud1idylkfqkoa2.cloudflarestream.com/58bd12339bafa1040d9b189e120f6f57/manifest/video.m3u8";
const HERO_POSTER_URL = "https://customer-izud1idylkfqkoa2.cloudflarestream.com/58bd12339bafa1040d9b189e120f6f57/thumbnails/thumbnail.jpg";

const ONTOUR_URL =
  "https://events.ontourhq.com/e/yrd/tickets?utm_source=affiliate&utm_name=MOTHERSDAY&utm_campaign=MOTHERSDAY&utm_id=69ff755d74c44832904113150a1e618c";

type Act = {
  song: string;
  also?: string[];
  artist: string;
  credit?: string;
  isHeadliner?: boolean;
};

type DayBill = {
  day: string;
  date: string;
  acts: Act[];
};

const lineup: DayBill[] = [
  {
    day: "Saturday",
    date: "July 18",
    acts: [
      { song: "Joy to the World", also: ["One", "Mama Told Me Not to Come"], artist: "Three Dog Night", credit: "Saturday headliner", isHeadliner: true },
      { song: "Biggest Part of Me", also: ["How Much I Feel"], artist: "Ambrosia" },
      { song: "Brandy (You're a Fine Girl)", artist: "Elliot Lurie", credit: "originally Looking Glass" },
      { song: "Baby Come Back", artist: "Peter Beckett", credit: "originally Player" },
      { song: "Magnet and Steel", artist: "Walter Egan" },
      { song: "I'd Really Love to See You Tonight", artist: "John Ford Coley" },
    ],
  },
  {
    day: "Friday",
    date: "July 17",
    acts: [
      { song: "Escape Artist", also: ["Step", "Tropical Illusion"], artist: "Yacht Rock Revue", credit: "Friday headliner", isHeadliner: true },
      { song: "Love Will Find a Way", also: ["Whatcha Gonna Do?"], artist: "Pablo Cruise" },
      { song: "Go Your Own Way", also: ["Dreams", "The Chain"], artist: "Seven Wonders", credit: "Fleetwood Mac tribute" },
    ],
  },
];

const ActRow = ({ song, also, artist, credit, isHeadliner }: Act) => (
  <motion.div
    className="py-7 md:py-9"
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
  >
    <p
      className={`text-yacht-charcoal leading-[1.05] italic ${
        isHeadliner ? "text-3xl md:text-5xl" : "text-2xl md:text-4xl"
      }`}
      style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400 }}
    >
      {song}
    </p>
    <p className="mt-3 text-yacht-blue text-xs md:text-sm font-bold tracking-[0.2em] uppercase">
      {artist}
      {credit && <span className="text-yacht-charcoal/50 font-medium normal-case tracking-normal italic"> · {credit}</span>}
    </p>
    {also && also.length > 0 && (
      <p className="mt-2 text-yacht-charcoal/45 text-[13px] md:text-sm italic" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
        also: {also.join(" · ")}
      </p>
    )}
  </motion.div>
);

const MothersDay = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  usePageSEO({
    title: "A Gift for Mom — Yacht Rock Detroit Tickets",
    description:
      "Get Mom tickets to Yacht Rock Detroit by Sunday. July 17–18, 2026 at The Roostertail. Three Dog Night, Pablo Cruise, Ambrosia, Yacht Rock Revue & more.",
    canonical: "https://yachtrockdetroit.com/mothers-day",
  });

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://events.ontourhq.com/ts_modal.js";
    script.async = true;
    script.onload = () => {
      (window as unknown as { TSModals?: { buildModal: (cfg: { url: string; modalTriggerElementId: string }) => void } }).TSModals?.buildModal({
        url: ONTOUR_URL,
        modalTriggerElementId: "mothers-day-trigger",
      });
    };
    document.head.appendChild(script);
    return () => {
      try {
        document.head.removeChild(script);
      } catch {
        /* noop */
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hls: Hls | null = null;

    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(HERO_VIDEO_URL);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = HERO_VIDEO_URL;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => {});
      });
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleGetTickets = () => {
    document.getElementById("mothers-day-trigger")?.click();
  };

  return (
    <div className="min-h-screen bg-yacht-cream">
      <button
        id="mothers-day-trigger"
        aria-hidden="true"
        style={{ position: "fixed", left: "-9999px", width: "1px", height: "1px", opacity: 0.01 }}
      />

      {/* HERO — video background, logo only, clean conversion */}
      <section className="relative min-h-screen overflow-hidden">
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${videoLoaded ? "opacity-0" : "opacity-100"}`}
          style={{ backgroundImage: `url(${HERO_POSTER_URL})` }}
        />

        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER_URL}
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
        />

        <div className="absolute inset-0 bg-black/45" />

        {/* Logo only — no nav, no top buy button */}
        <div className="absolute top-0 left-0 right-0 z-20 px-6 pt-8 md:pt-10 flex justify-center">
          <a href="/" className="block">
            <img src={logoWhite} alt="Yacht Rock Detroit" className="h-20 md:h-28 w-auto" />
          </a>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-44 md:pt-44 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              className="text-yacht-yellow text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              For Mom · Get It By Sunday
            </motion.p>

            <motion.h1
              className="text-white font-bold leading-[0.95] mb-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span
                className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
                style={{ fontFamily: "Georgia, Times New Roman, serif", fontWeight: 300 }}
              >
                She'll know
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl italic uppercase tracking-wide mt-3">
                Every Song.
              </span>
            </motion.h1>

            <motion.p
              className="text-white/85 text-lg md:text-xl font-light mb-3 max-w-2xl mx-auto"
              style={{ fontFamily: "Georgia, Times New Roman, serif" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Three Dog Night · Pablo Cruise · Ambrosia · Yacht Rock Revue · & more
            </motion.p>
            <motion.p
              className="text-yacht-yellow/95 text-base md:text-lg font-semibold tracking-wide mb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              July 17–18, 2026 · The Roostertail · Detroit
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col items-center"
            >
              <button
                onClick={handleGetTickets}
                className="btn-primary px-10 md:px-14 py-5 md:py-6 text-lg md:text-2xl font-bold tracking-wide uppercase"
              >
                Get Mom Her Tickets
              </button>
              <p className="text-white/60 text-xs md:text-sm mt-5 italic">
                From $149 · Single day or full weekend · Sent instantly
              </p>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 left-8 z-20">
          <VolumeToggle isMuted={isMuted} onClick={toggleMute} />
        </div>
      </section>

      {/* LINEUP — songs Mom knows, mirrors homepage conversion pattern */}
      <section className="relative py-20 md:py-28 px-6 bg-yacht-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-yacht-blue text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-4">
              ★ The Lineup ★
            </p>
            <h2 className="text-yacht-charcoal text-4xl md:text-6xl font-bold mb-5 leading-[1.05]">
              You'll know every song.
            </h2>
            <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto">
              Sung by the original artists. Detroit River. July 17–18.
            </p>
          </motion.div>

          <div className="space-y-12 md:space-y-16">
            {lineup.map((day) => (
              <div key={day.day}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-px flex-1 bg-yacht-charcoal/20" />
                  <p className="text-yacht-charcoal text-xs md:text-sm font-bold tracking-[0.3em] uppercase whitespace-nowrap">
                    {day.day}, {day.date}
                  </p>
                  <div className="h-px flex-1 bg-yacht-charcoal/20" />
                </div>

                <div>
                  {day.acts.map((act) => (
                    <ActRow key={act.artist} {...act} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <motion.div
            className="mt-12 md:mt-16 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p
              className="text-yacht-charcoal/70 text-base md:text-lg italic mb-8"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              9 national acts. 30+ songs she's heard a thousand times.
              <br className="hidden md:block" />
              <span className="text-yacht-charcoal/50"> Plus DJs &amp; local bands.</span>
            </p>

            <button
              onClick={handleGetTickets}
              className="inline-block px-10 py-4 rounded-full bg-yacht-blue text-white font-bold text-base tracking-wide uppercase transition-all hover:brightness-110"
              style={{ boxShadow: "0 4px 14px hsla(193, 52%, 47%, 0.3)" }}
            >
              Get Mom Her Tickets
            </button>
          </motion.div>
        </div>
      </section>

      {/* REELS — actual fan content from last year */}
      <LastYearSection />

      {/* FINAL CTA — homepage TicketCTA style, on-brand */}
      <section className="relative py-20 md:py-28 px-6 bg-yacht-blue overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative max-w-xl mx-auto text-center">
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yacht-yellow/15 border border-yacht-yellow/30 mb-7"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="w-2 h-2 rounded-full bg-yacht-yellow animate-pulse" />
            <span className="text-yacht-yellow text-[11px] md:text-xs font-bold uppercase tracking-[0.25em]">
              Get It By Sunday
            </span>
          </motion.div>

          <motion.h2
            className="text-yacht-yellow font-bold text-6xl md:text-7xl leading-none mb-3"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            From $149
          </motion.h2>

          <motion.p
            className="text-white/80 text-sm md:text-base font-semibold tracking-[0.25em] uppercase mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Both Days · For Mom
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <button
              onClick={handleGetTickets}
              className="inline-block px-14 py-5 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-lg md:text-xl tracking-wide uppercase transition-all hover:brightness-110"
              style={{ boxShadow: "0 4px 18px hsla(54, 93%, 49%, 0.4)" }}
            >
              Get Mom Her Tickets
            </button>
          </motion.div>

          <motion.p
            className="mt-7 text-white/65 text-xs md:text-sm font-medium tracking-wide"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Sent instantly. Print or text the QR — wrap it up by Sunday morning.
          </motion.p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MothersDay;
